import React, { useState, useEffect, useRef } from 'react';
import './StepEndOfDay_v2.scss';
import { Tooltip } from 'antd';
import { Info as InfoIcon } from 'lucide-react';
import { TOOLTIP_CONFIG, getTooltipContent } from './tooltipConfig';
import { C } from "../../components/ui";
import {
  Journal,
  EndofDaySummary,
  PostTradeData,
  PostTradeErrorSummary,
  FormConfigs,
  UILabels,
  UpdateDailyJournalPayload,
  PostTradeTooltipData
} from './type';
import { getEndOfDaySummary, getPostTradeJournalHistory, updateDailyChecklist, getToolTip, getDailyChecklist } from './function';
import { fetchChecklistHistory } from 'services/thirteenWeeks';
import dayjs from 'dayjs';
import EodSummaryCard, { parseEodNotes } from './EodSummaryCard';
import { useAppNotification } from "hooks/useNotification";

const getScoreColor = (score: number, max: number) => {
  const pct = max > 0 ? score / max : 0;
  if (pct >= 0.85) return "oklch(0.45 0.13 145)";
  if (pct >= 0.5) return "oklch(0.55 0.1 75)";
  return "oklch(0.5 0.16 25)";
};

interface TradeScoreCardProps {
  tradeData: any; // Truyền biến `t` hoặc `r` vào đây
  maxScore?: number;
  label?: string;
}

export const TradeScoreCard: React.FC<TradeScoreCardProps> = ({
  tradeData,
  maxScore = 100,
  label = 'Điểm tổng lệnh',
}) => {
  // --- HÀM TÍNH TỔNG ĐIỂM AN TOÀN ---
  const calculateTotalScore = (): number => {
    if (!tradeData) return 0;

    // Trường hợp 1: Nếu backend/đối tượng đã có sẵn tổng điểm
    if (typeof tradeData.totalScore === 'number') return tradeData.totalScore;
    if (typeof tradeData.total_score === 'number') return tradeData.total_score;
    if (typeof tradeData.result_evaluation?.totalScore === 'number') return tradeData.result_evaluation.totalScore;

    // Trường hợp 2: Nếu có các điểm số thành phần dạng số (setupScore, riskScore, psyScore)
    const setupScore = tradeData.setupScore ?? tradeData.result_evaluation?.setupScore;
    const riskScore = tradeData.riskScore ?? tradeData.result_evaluation?.riskScore;
    const psyScore = tradeData.psyScore ?? tradeData.result_evaluation?.psyScore;

    if (typeof setupScore === 'number' && typeof riskScore === 'number' && typeof psyScore === 'number') {
      return setupScore + riskScore + psyScore;
    }

    // Trường hợp 3: Tính tổng thủ công từ các danh sách setup_block & psychology_block + risk_block
    let total = 0;

    // Tính điểm Setup Block
    if (Array.isArray(tradeData.setup_block)) {
      total += tradeData.setup_block.reduce((sum: number, item: any) => {
        const score = parseFloat(item.score_text || item.score || 0);
        return sum + (isNaN(score) ? (item.is_passed ? 10 : 0) : score);
      }, 0);
    }

    // Tính điểm Risk Block
    if (tradeData.risk_block) {
      if (tradeData.risk_block.has_sl) total += 30; // Mặc định 30đ nếu có SL
    }

    // Tính điểm Psychology Block
    if (Array.isArray(tradeData.psychology_block)) {
      total += tradeData.psychology_block.reduce((sum: number, item: any) => {
        const score = parseFloat(item.score_text || item.score || 0);
        return sum + (isNaN(score) ? (item.is_passed ? 10 : 0) : score);
      }, 0);
    }

    return Math.min(total, maxScore);
  };

  const score = calculateTotalScore();

  // Cấu hình vòng SVG
  const size = 44;
  const strokeWidth = 4;
  const center = size / 2;
  const radius = center - strokeWidth;
  const circumference = 2 * Math.PI * radius;

  const percentage = Math.min(Math.max(score / maxScore, 0), 1);
  const strokeDashoffset = circumference - percentage * circumference;

  // Tự động đổi màu vòng tròn theo mức điểm
  const getProgressColor = (val: number) => {
    if (val >= 80) return '#00c39a'; // Xanh lá
    if (val >= 50) return '#f59e0b'; // Vàng cam
    return '#ef4444'; // Đỏ
  };

  const color = getProgressColor(score);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '12px 16px',
        background: '#fff',
        border: '1px solid oklch(0.92 0.005 90)',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.02)',
      }}
    >
      {/* Vòng tròn Progress SVG */}
      <div style={{ position: 'relative', width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="transparent"
            stroke="#eef2f6"
            strokeWidth={strokeWidth}
          />
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="transparent"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            style={{ transition: 'stroke-dashoffset 0.5s ease-in-out' }}
          />
        </svg>
        <span style={{ position: 'absolute', fontSize: '11px', fontWeight: 800, color: '#1a1a1a' }}>
          {score}
        </span>
      </div>

      {/* Thông tin chữ */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
        <span style={{ fontSize: '12px', color: '#666', fontWeight: 500 }}>
          {label}
        </span>
        <div style={{ fontSize: '15px', fontWeight: 800, color: '#0f172a' }}>
          <span>{score}</span>
          <span style={{ color: '#94a3b8' }}>/{maxScore}</span>
        </div>
      </div>
    </div>
  );
};

interface StepEndOfDay_v2Props {
  date?: string;
  account_number?: string;
  symbol?: string;
  journal_data?: Journal;
  form_configs?: FormConfigs;
  ui_labels?: UILabels;
  can_edit?: boolean;
  onReload?: () => void;
  /** Jump to another journal step (used by the "Điền ngay" shortcuts of the day summary) */
  onGoToStep?: (step: 0 | 1 | 2 | 3 | 4) => void;
}

const StepEndOfDay_v2: React.FC<StepEndOfDay_v2Props> = ({
  date,
  account_number,
  journal_data,
  form_configs,
  ui_labels,
  can_edit = false,
  onReload,
  onGoToStep
}) => {
  // States lưu dữ liệu từ API
  const [eodSummary, setEodSummary] = useState<EndofDaySummary | null>(null);
  const [postTradeList, setPostTradeList] = useState<PostTradeData[]>([]);
  const [errorSummary, setErrorSummary] = useState<PostTradeErrorSummary | null>(null);
  const [tooltips, setTooltips] = useState<PostTradeTooltipData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const notification = useAppNotification();

  // Fetch Tooltips khi mount
  useEffect(() => {
    getToolTip().then((res) => {
      if (res?.success && res.data) {
        setTooltips(res.data);
      }
    }).catch(err => console.error("Lỗi khi tải Tooltips:", err));
  }, []);

  // States UI
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [expandedStages, setExpandedStages] = useState<Set<number>>(new Set());

  // ===== States cho Phần 5 (Bài học & cam kết) =====
  const [noteBrokenPlan, setNoteBrokenPlan] = useState<string>('');
  const [noteDeviation, setNoteDeviation] = useState<string>('');
  const [noteCommitment, setNoteCommitment] = useState<string>('');
  const [showErrors, setShowErrors] = useState<boolean>(false);
  const reflectionRef = useRef<HTMLDivElement>(null);
  const ordersRef = useRef<HTMLDivElement>(null);

  // Commitment written on the latest earlier journal day, shown in the summary to check it against today.
  // Only days already in the history are read: GET /checklist?date= would create an empty journal otherwise.
  const [prevCommitment, setPrevCommitment] = useState<{ date: string; text: string } | null>(null);
  useEffect(() => {
    if (!date) return;
    let cancelled = false;
    (async () => {
      try {
        const d = dayjs(date);
        const months = [d.format('YYYY-MM'), d.subtract(1, 'month').format('YYYY-MM')];
        const lists = await Promise.all(months.map((m) => fetchChecklistHistory(m)));
        const prevDates = lists.flat().map((h) => h.date).filter((x) => x < date).sort().reverse().slice(0, 5);
        for (const prev of prevDates) {
          const res = await getDailyChecklist(prev);
          const text = parseEodNotes(res?.journal).commitment;
          if (text) {
            if (!cancelled) setPrevCommitment({ date: prev, text });
            return;
          }
        }
        if (!cancelled) setPrevCommitment(null);
      } catch {
        if (!cancelled) setPrevCommitment(null);
      }
    })();
    return () => { cancelled = true; };
  }, [date]);

  // 1. Fetch API khi date hoặc account_number thay đổi
  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      if (!account_number) return;
      setLoading(true);
      try {
        const [summaryRes, postTradeRes] = await Promise.all([
          getEndOfDaySummary(account_number, date),
          getPostTradeJournalHistory(account_number, undefined, undefined, date)
        ]);

        if (isMounted) {
          if (summaryRes?.success && summaryRes.data) {
            setEodSummary(summaryRes.data);
          } else {
            setEodSummary(null);
          }

          if (postTradeRes?.success) {
            setPostTradeList(postTradeRes.data || []);
            // Lấy trực tiếp error_summary từ kết quả trả về của API
            setErrorSummary(postTradeRes.error_summary || null);
          } else {
            setPostTradeList([]);
            setErrorSummary(null);
          }
        }
      } catch (err) {
        console.error("Lỗi khi tải dữ liệu EOD:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchData();
    return () => {
      isMounted = false;
    };
  }, [date, account_number]);

  // 2. Load dữ liệu từ journal_data vào Phần 5 khi journal_data truyền vào
  useEffect(() => {
    if (!journal_data?.eod_extra_data) return;

    try {
      const parsedExtra = typeof journal_data.eod_extra_data === 'string'
        ? JSON.parse(journal_data.eod_extra_data)
        : journal_data.eod_extra_data;

      let brokenPlan = '';
      let deviation = '';
      let commitment = '';

      if (parsedExtra?.summary_note) {
        try {
          const parsedSummaryNote = typeof parsedExtra.summary_note === 'string'
            ? JSON.parse(parsedExtra.summary_note)
            : parsedExtra.summary_note;

          const eodReview = parsedSummaryNote?.eod_review_data;

          if (eodReview) {
            brokenPlan = eodReview.note_broken_plan || '';
            deviation = eodReview.note_deviation || '';
            commitment = eodReview.note_commitment || '';
          }
        } catch (innerErr) {
          console.error("Lỗi parse inner summary_note:", innerErr);
        }
      }

      setNoteBrokenPlan(brokenPlan || parsedExtra.worst_thing || '');
      setNoteDeviation(deviation || '');
      setNoteCommitment(commitment || parsedExtra.best_thing || '');

    } catch (err) {
      console.error("Lỗi parse eod_extra_data:", err);
    }
  }, [journal_data]);

  // Handlers mở/đóng dropdown
  const toggleStageExpand = (stageIndex: number) => {
    const newExpanded = new Set(expandedStages);
    if (newExpanded.has(stageIndex)) newExpanded.delete(stageIndex);
    else newExpanded.add(stageIndex);
    setExpandedStages(newExpanded);
  };

  const toggleExpand = (id: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) newExpanded.delete(id);
    else newExpanded.add(id);
    setExpandedRows(newExpanded);
  };

  // ===== Handler Lưu dữ liệu tổng kết ngày =====
  const handleSaveEodReview = async () => {
    if (!noteBrokenPlan.trim() || !noteDeviation.trim() || !noteCommitment.trim()) {
      setShowErrors(true);
      notification.warning('Vui lòng nhập đầy đủ thông tin Bài học & cam kết (mục 5)!');
      reflectionRef.current?.scrollIntoView({ behavior: 'smooth' });
      return;
    }

    setIsSaving(true);
    try {
      // Only the three notes go into summary_note. Spreading the previous eod_extra_data here (which is the whole
      // last payload, summary_note included) nested every earlier save inside the new one: 607 → 2,369 chars
      // after three saves, growing with every click. The loader above reads summary_note.eod_review_data.
      const updatedExtra = {
        eod_review_data: {
          note_broken_plan: noteBrokenPlan,
          note_deviation: noteDeviation,
          note_commitment: noteCommitment,
        }
      };

      const payload: UpdateDailyJournalPayload = {
        date: date,
        eod_review_data: {
          all_trades_logged: journal_data?.eod_all_trades_logged ?? true,
          kpi_updated: journal_data?.eod_kpi_updated ?? true,
          reviewed: true,
          summary_note: JSON.stringify(updatedExtra),
          quality_setup: true,
          quality_risk: true,
          quality_fomo: false,
          quality_management: true,
          quality_repeatable: true,
          score_discipline: journal_data?.score_discipline || 0,
          score_plan_adherence: journal_data?.score_plan_adherence || 0,
          score_risk_management: journal_data?.score_risk_management || 0,
          score_psychology: journal_data?.score_psychology || 0,
          total_eod_score: eodSummary?.compliance_score.total_score || 0,
          eod_grade: journal_data?.eod_grade || '',
          best_thing: noteCommitment,
          worst_thing: noteBrokenPlan
        }
      };

      const res = await updateDailyChecklist(payload);
      if (res) {
        setShowErrors(false);
        notification.success('Lưu tổng kết ngày thành công!');
        if (onReload) onReload();
      }
    } catch (error) {
      console.error("Lỗi khi lưu tổng kết:", error);
      notification.error('Lưu tổng kết ngày thất bại. Vui lòng thử lại.');
    } finally {
      setIsSaving(false);
    }
  };

  // Helper tính màu điểm số
  const scoreColor = (s: number) => {
    if (s >= 80) return 'var(--green)';
    if (s >= 50) return 'var(--amber)';
    return 'var(--red)';
  };

  // SVG Gauge calculated values
  const circumference = 490;
  const dayScore = eodSummary?.compliance_score.total_score || 0;
  const stageTotal = (eodSummary?.phases_breakdown || []).reduce((sum, p) => sum + (Number(p.earned_score) || 0), 0);
  const strokeDashoffset = circumference - (circumference * dayScore / 100);

  // Trích xuất trực tiếp thông tin lỗi từ error_summary (thay vì tự tính)
  const noSlCount = errorSummary?.no_sl?.count || 0;
  const noSlTrades = errorSummary?.no_sl?.trades || [];

  const movedSlCount = errorSummary?.moved_sl?.count || 0;
  const movedSlTrades = errorSummary?.moved_sl?.trades || [];

  const closedEarlyCount = errorSummary?.closed_early?.count || 0;
  const closedEarlyTrades = errorSummary?.closed_early?.trades || [];

  if (loading) {
    return <div className="step-eod-v2-loading" style={{ padding: '24px', textAlign: 'center' }}>Đang tải dữ liệu tổng kết ngày...</div>;
  }

  return (
    <div className="step-eod-v2">
      {/* ==================== DAY SUMMARY: overview → trades → evaluation → lesson & save → reference detail ==================== */}
      <EodSummaryCard
        eodSummary={eodSummary}
        errorSummary={errorSummary}
        stageTotal={stageTotal}
        complianceScore={dayScore}
        reflectionDone={(() => { const n = parseEodNotes(journal_data); return !!(n.brokenPlan && n.deviation && n.commitment); })()}
        prevCommitment={prevCommitment}
        onGoToStep={onGoToStep}
        onGoToReflection={() => reflectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
        onGoToOrders={() => ordersRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
      />

      {/* ==================== 2. ORDER STATUS — what happened with each trade ==================== */}
      <div className="card" ref={ordersRef}>
        <div className="card-head">
          <div>
            <h2><span className="num">2</span>Tình trạng lệnh</h2>
            <p>Quét nhanh 3 lỗi hay gặp nhất trong ngày theo lịch sử nhật ký vị thế post-trade.</p>
          </div>
        </div>

        <div className="order-mini">
          {postTradeList.length === 0 ? (
            <div style={{ padding: '16px', color: '#666', textAlign: 'center' }}>Chưa có lịch sử lệnh trong ngày</div>
          ) : (
            postTradeList.map((t: any) => {
              const badges = [];
              if (t.risk_block && !t.risk_block.has_sl) {
                badges.push(<span className="om-badge" key="nosl">Không SL</span>);
              }
              if (t.risk_block && t.risk_block.modify_count > 0) {
                badges.push(<span className="om-badge" key="movesl"> × Dời SL: {t.risk_block.modify_count} lần</span>);
              }

              if (t.trade_details && t.result_evaluation?.status === 'WIN') {
                const { planned_side, actual_side, planned_tp, close_price } = t.trade_details;
                const side = actual_side || planned_side;
                if ((side === 'BUY' && close_price < planned_tp) || (side === 'SELL' && close_price > planned_tp)) {
                  badges.push(<span className="om-badge" key="early">Chốt sớm</span>);
                }
              }

              if (t.psychology_block && t.psychology_block[1]?.is_passed === false) {
                badges.push(<span className="om-badge" key="fomo">Chốt non</span>);
              }

              if (badges.length === 0) badges.push(<span className="om-badge ok" key="ok">✓ Tốt</span>);
              const isExpanded = expandedRows.has(t.position_id);

              const r = t.header_scores || t;

              return (
                <div key={t.position_id} className="om-item">
                  <div className={`om-row ${isExpanded ? 'is-expanded' : ''}`} onClick={() => toggleExpand(t.position_id)}>
                    <button className="expand-btn">{isExpanded ? '▾' : '▸'}</button>
                    <span className="om-pair">{t.symbol} ({t.side})</span>
                    <span className="om-id">#{t.position_id}</span>
                    <span className="om-badges">{badges}</span>
                  </div>

                  {isExpanded && (
                    <div style={{ padding: '18px 12px 46px', background: 'oklch(0.985 0.003 90)', display: 'flex', flexDirection: 'column', gap: '16px', fontSize: '13px' }}>
                      <div className="eod-breakdown" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '14px' }}>
                        {/* Setup Block */}
                        <div style={{ border: '1px solid oklch(0.92 0.005 90)', borderRadius: '12px', padding: '12px 14px', background: '#fff' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <span style={{ fontSize: '12px', fontWeight: 700, color: 'oklch(0.4 0.01 90)', textTransform: 'uppercase', letterSpacing: '0.02em' }}>
                                Setup
                              </span>
                              {getTooltipContent(tooltips, TOOLTIP_CONFIG.setupHeader) && (
                                <Tooltip
                                  title={getTooltipContent(tooltips, TOOLTIP_CONFIG.setupHeader)?.text}
                                  placement={getTooltipContent(tooltips, TOOLTIP_CONFIG.setupHeader)?.placement || "top"}
                                >
                                  <InfoIcon size={14} style={{ cursor: 'pointer', color: 'oklch(0.55 0.01 90)' }} />
                                </Tooltip>
                              )}
                            </div>
                            <div style={{ fontSize: '13px', fontWeight: 800, color: getScoreColor(r?.setup, 30) }}>{r?.setup ?? 0}/30</div>
                          </div>
                          {t.setup_block?.map((item: any, idx: number) => {
                            const itemTooltipKey = TOOLTIP_CONFIG.setupItems[idx];
                            const ttContent = getTooltipContent(tooltips, itemTooltipKey);
                            return (
                              <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '6px', padding: '3px 0', color: item.is_passed ? 'oklch(0.45 0.13 145)' : 'oklch(0.5 0.16 25)' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                  <Tooltip title={ttContent?.text} placement={ttContent?.placement || "top"}>
                                    <span>{item.is_passed ? '✓' : '✕'} {item.label}</span>
                                  </Tooltip>
                                </div>
                                <span style={{ fontWeight: 600 }}>{item.score_text}</span>
                              </div>
                            );
                          })}
                        </div>

                        {/* Risk Block */}
                        <div style={{ border: '1px solid oklch(0.92 0.005 90)', borderRadius: '12px', padding: '12px 14px', background: '#fff' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <span style={{ fontSize: '12px', fontWeight: 700, color: 'oklch(0.4 0.01 90)', textTransform: 'uppercase', letterSpacing: '0.02em' }}>
                                Risk
                              </span>
                              {getTooltipContent(tooltips, TOOLTIP_CONFIG.riskHeader) && (
                                <Tooltip
                                  title={getTooltipContent(tooltips, TOOLTIP_CONFIG.riskHeader)?.text}
                                  placement={getTooltipContent(tooltips, TOOLTIP_CONFIG.riskHeader)?.placement || "top"}
                                >
                                  <InfoIcon size={14} style={{ cursor: 'pointer', color: 'oklch(0.55 0.01 90)' }} />
                                </Tooltip>
                              )}
                            </div>
                            <div style={{ fontSize: '13px', fontWeight: 800, color: getScoreColor(r?.risk, 30) }}>{r?.risk ?? 0}/30</div>
                          </div>
                          {t.risk_block && (() => {
                            const ttContent = getTooltipContent(tooltips, TOOLTIP_CONFIG.riskItems[0]);
                            return (
                              <div style={{ fontSize: '12.5px', lineHeight: '1.5', color: 'oklch(0.35 0.01 90)' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                  <Tooltip title={ttContent?.text} placement={ttContent?.placement || "top"}>
                                    <span>{t.risk_block.has_sl ? '✅' : '❌'} {t.risk_block.message}</span>
                                  </Tooltip>
                                </div>
                              </div>
                            );
                          })()}
                        </div>

                        {/* Psychology Block */}
                        <div style={{ border: '1px solid oklch(0.92 0.005 90)', borderRadius: '12px', padding: '12px 14px', background: '#fff' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <span style={{ fontSize: '12px', fontWeight: 700, color: 'oklch(0.4 0.01 90)', textTransform: 'uppercase', letterSpacing: '0.02em' }}>
                                Tâm lý
                              </span>
                              {getTooltipContent(tooltips, TOOLTIP_CONFIG.tamlyHeader) && (
                                <Tooltip
                                  title={getTooltipContent(tooltips, TOOLTIP_CONFIG.tamlyHeader)?.text}
                                  placement={getTooltipContent(tooltips, TOOLTIP_CONFIG.tamlyHeader)?.placement || "top"}
                                >
                                  <InfoIcon size={14} style={{ cursor: 'pointer', color: 'oklch(0.55 0.01 90)' }} />
                                </Tooltip>
                              )}
                            </div>
                            <div style={{ fontSize: '13px', fontWeight: 800, color: getScoreColor(r?.psychology, 40) }}>{r?.psychology ?? 0}/40</div>
                          </div>
                          {t.psychology_block?.map((item: any, idx: number) => {
                            const itemTooltipKey = TOOLTIP_CONFIG.tamlyItems[idx];
                            const ttContent = getTooltipContent(tooltips, itemTooltipKey);
                            return (
                              <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '6px', padding: '3px 0', color: item.is_passed ? 'oklch(0.45 0.13 145)' : 'oklch(0.5 0.16 25)' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                  <Tooltip title={ttContent?.text} placement={ttContent?.placement || "top"}>
                                    <span>{item.is_passed ? '✓' : '✕'} {item.label}</span>
                                  </Tooltip>
                                </div>
                                <span style={{ fontWeight: 600 }}>{item.score_text}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      <TradeScoreCard tradeData={t} maxScore={100} />
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
        <div className="violation-grid">
          {/* 1. Không cài Stop Loss */}
          <div className={`vbox vb-nosl ${noSlCount === 0 ? 'empty' : ''}`}>
            <div className="vb-head">🚫 Không cài Stop Loss</div>
            <div className="vb-count">{noSlCount} lệnh</div>
            <div className="vb-list">
              {noSlCount > 0 ? noSlTrades.map((tradeInfo, idx) => (
                <div key={idx} className="vb-item">
                  {tradeInfo}
                </div>
              )) : <div className="vb-empty">✓ Không có lệnh nào vi phạm</div>}
            </div>
          </div>

          {/* 2. Dời Stop Loss */}
          <div className={`vbox vb-movesl ${movedSlCount === 0 ? 'empty' : ''}`}>
            <div className="vb-head">↔ Dời Stop Loss</div>
            <div className="vb-count">{movedSlCount} lệnh</div>
            <div className="vb-list">
              {movedSlCount > 0 ? movedSlTrades.map((tradeInfo, idx) => (
                <div key={idx} className="vb-item">
                  {tradeInfo}
                </div>
              )) : <div className="vb-empty">✓ Không có lệnh nào vi phạm</div>}
            </div>
          </div>

          {/* 3. Chốt lời sớm */}
          <div className={`vbox vb-early ${closedEarlyCount === 0 ? 'empty' : ''}`}>
            <div className="vb-head">⏱ Chốt lời sớm</div>
            <div className="vb-count">{closedEarlyCount} lệnh</div>
            <div className="vb-list">
              {closedEarlyCount > 0 ? closedEarlyTrades.map((tradeInfo, idx) => (
                <div key={idx} className="vb-item">
                  {tradeInfo}
                </div>
              )) : <div className="vb-empty">✓ Không có lệnh nào vi phạm</div>}
            </div>
          </div>
        </div>
      </div>

      {/* ==================== 3. QUADRANT — setup quality vs result ==================== */}
      <div className="card">
        <div className="card-head">
          <div>
            <h2><span className="num">3</span>Ma trận Setup × Kết quả</h2>
            <p>Phân loại hành vi — góc đáng lo nhất là "Thắng nhưng sai setup".</p>
          </div>
        </div>
        <div className="quad-grid">
          <div className="quad q-good">
            <div className="qt">{eodSummary?.setup_matrix.good_win.label || 'Đúng setup · Thắng'}</div>
            <div className="qn">{eodSummary?.setup_matrix.good_win.count || 0}</div>
            <div className="qd">{eodSummary?.setup_matrix.good_win.desc || 'Lệnh mẫu — lệnh chuẩn để tham khảo.'}</div>
          </div>
          <div className="quad q-warn">
            <div className="qt">{eodSummary?.setup_matrix.bad_win.label || 'Sai setup · Thắng'}</div>
            <div className="qn">{eodSummary?.setup_matrix.bad_win.count || 0}</div>
            <div className="qd">{eodSummary?.setup_matrix.bad_win.desc || 'Thắng "may rủi" — không được thưởng điểm.'}</div>
          </div>
          <div className="quad q-ok">
            <div className="qt">{eodSummary?.setup_matrix.good_loss.label || 'Đúng setup · Thua'}</div>
            <div className="qn">{eodSummary?.setup_matrix.good_loss.count || 0}</div>
            <div className="qd">{eodSummary?.setup_matrix.good_loss.desc || 'Lỗ hợp lệ — chi phí kinh doanh bình thường.'}</div>
          </div>
          <div className="quad q-danger">
            <div className="qt">{eodSummary?.setup_matrix.bad_loss.label || 'Sai setup · Thua'}</div>
            <div className="qn">{eodSummary?.setup_matrix.bad_loss.count || 0}</div>
            <div className="qd">{eodSummary?.setup_matrix.bad_loss.desc || 'Vi phạm kép — cần xem lại ở mục dưới.'}</div>
          </div>
        </div>
      </div>

      {/* ==================== 4. SCORE HERO — compliance of the trades ==================== */}
      <div className="card">
        <div className="card-head">
          <div>
            <h2><span className="num">4</span>Điểm tuân thủ khi vào lệnh</h2>
            <p>Chấm theo QUY TRÌNH bạn đã thực hiện — không liên quan đến lãi/lỗ.</p>
          </div>
        </div>

        <div className="score-hero">
          <div className="gauge-wrap">
            <svg width="180" height="180" viewBox="0 0 180 180">
              <circle cx="90" cy="90" r="78" fill="none" stroke="#EEF1F5" strokeWidth="16" />
              <circle cx="90" cy="90" r="78" fill="none" stroke={scoreColor(dayScore)} strokeWidth="16"
                strokeDasharray={circumference} strokeDashoffset={strokeDashoffset} strokeLinecap="round" transform="rotate(-90 90 90)"
                style={{ transition: 'stroke-dashoffset 0.6s ease' }} />
            </svg>
            <div className="gauge-label">
              <div className="n">{dayScore}</div>
              <div className="l">/ 100 ĐIỂM</div>
            </div>
          </div>

          <div className="score-breakdown">
            <div className="bd-row">
              <div className="lbl">{eodSummary?.compliance_score.categories.setup.label || 'Đúng Setup'}</div>
              <div className="track">
                <div className="fill" style={{ width: `${eodSummary?.compliance_score.categories.setup.percent || 0}%`, background: '#2E5CE6' }}></div>
              </div>
              <div className="pct">{eodSummary?.compliance_score.categories.setup.percent || 0}%</div>
            </div>
            <div className="bd-row">
              <div className="lbl">{eodSummary?.compliance_score.categories.risk.label || 'Quản trị rủi ro'}</div>
              <div className="track">
                <div className="fill" style={{ width: `${eodSummary?.compliance_score.categories.risk.percent || 0}%`, background: 'var(--amber)' }}></div>
              </div>
              <div className="pct">{eodSummary?.compliance_score.categories.risk.percent || 0}%</div>
            </div>
            <div className="bd-row">
              <div className="lbl">{eodSummary?.compliance_score.categories.psychology.label || 'Kỷ luật tâm lý'}</div>
              <div className="track">
                <div className="fill" style={{ width: `${eodSummary?.compliance_score.categories.psychology.percent || 0}%`, background: '#9B5DE5' }}></div>
              </div>
              <div className="pct">{eodSummary?.compliance_score.categories.psychology.percent || 0}%</div>
            </div>
          </div>

          <div className="pnl-box">
            <div className="l">Kết quả P&L ngày</div>
            <div className="n" style={{ color: (eodSummary?.compliance_score.pnl.is_profit ?? true) ? 'var(--green)' : 'var(--red)' }}>
              {eodSummary?.compliance_score.pnl.formatted || '$0.00'}
            </div>
            <div className="note">Chỉ số riêng biệt — không cộng vào điểm tuân thủ</div>
          </div>
        </div>
      </div>

      {/* ==================== 5. REFLECTION — the part the trader fills in, saved right below ==================== */}
      <div className="card" ref={reflectionRef}>
        <div className="card-head">
          <div>
            <h2><span className="num">5</span>Bài học & cam kết</h2>
            <p>Bắt buộc với mọi lệnh rơi vào ô "Sai setup".</p>
          </div>
          <span className="badge" style={{
            background: (noteBrokenPlan.trim() && noteDeviation.trim() && noteCommitment.trim())
              ? 'var(--green-soft)'
              : (showErrors ? 'var(--red-soft)' : 'var(--gray-soft)'),
            color: (noteBrokenPlan.trim() && noteDeviation.trim() && noteCommitment.trim())
              ? 'var(--green)'
              : (showErrors ? 'var(--red)' : 'var(--muted)'),
            border: showErrors && !(noteBrokenPlan.trim() && noteDeviation.trim() && noteCommitment.trim()) ? '1px solid #fca5a5' : undefined
          }}>
            {(noteBrokenPlan.trim() && noteDeviation.trim() && noteCommitment.trim()) ? 'Đã hoàn thành' : (showErrors ? 'Chưa nhập đủ' : 'Chưa hoàn tất')}
          </span>
        </div>

        {showErrors && (!noteBrokenPlan.trim() || !noteDeviation.trim() || !noteCommitment.trim()) && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '10px 14px',
            background: 'var(--red-soft)',
            border: '1px solid #fca5a5',
            borderRadius: 10,
            color: 'var(--red)',
            fontSize: 13,
            marginBottom: 16
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <span>Vui lòng điền đầy đủ các thông tin trong phần <b>Bài học & cam kết</b> trước khi lưu.</span>
          </div>
        )}

        <div className="grid2">
          <div>
            <label className="field" style={{ color: (showErrors && !noteBrokenPlan.trim()) ? 'var(--red)' : undefined }}>
              Điểm nào trong kế hoạch đã bị phá vỡ hôm nay?
            </label>
            <textarea
              placeholder="VD: Vào lệnh SELL trước khi trigger xác nhận xuất hiện trên H1..."
              value={noteBrokenPlan}
              onChange={(e) => setNoteBrokenPlan(e.target.value)}
              style={{
                borderColor: (showErrors && !noteBrokenPlan.trim()) ? 'var(--red)' : undefined,
                boxShadow: (showErrors && !noteBrokenPlan.trim()) ? '0 0 0 1px var(--red)' : undefined
              }}
            ></textarea>
            {showErrors && !noteBrokenPlan.trim() && (
              <span style={{ color: 'var(--red)', fontSize: 12, marginTop: 4, display: 'block' }}>
                Vui lòng nhập điểm trong kế hoạch đã bị phá vỡ
              </span>
            )}
          </div>
          <div>
            <label className="field" style={{ color: (showErrors && !noteDeviation.trim()) ? 'var(--red)' : undefined }}>
              Điều gì khiến bạn lệch khỏi kế hoạch?
            </label>
            <textarea
              placeholder="VD: Thấy giá chạy nhanh, sợ bỏ lỡ (FOMO)..."
              value={noteDeviation}
              onChange={(e) => setNoteDeviation(e.target.value)}
              style={{
                borderColor: (showErrors && !noteDeviation.trim()) ? 'var(--red)' : undefined,
                boxShadow: (showErrors && !noteDeviation.trim()) ? '0 0 0 1px var(--red)' : undefined
              }}
            ></textarea>
            {showErrors && !noteDeviation.trim() && (
              <span style={{ color: 'var(--red)', fontSize: 12, marginTop: 4, display: 'block' }}>
                Vui lòng nhập điều khiến bạn lệch khỏi kế hoạch
              </span>
            )}
          </div>
        </div>

        <div className="grid2" style={{ marginTop: '16px' }}>
          <div>
            <label className="field" style={{ color: (showErrors && !noteCommitment.trim()) ? 'var(--red)' : undefined }}>
              Cam kết cụ thể cho ngày mai
            </label>
            <textarea
              placeholder="VD: Chỉ vào lệnh khi đủ 3/3 điều kiện trigger đã liệt kê..."
              value={noteCommitment}
              onChange={(e) => setNoteCommitment(e.target.value)}
              style={{
                borderColor: (showErrors && !noteCommitment.trim()) ? 'var(--red)' : undefined,
                boxShadow: (showErrors && !noteCommitment.trim()) ? '0 0 0 1px var(--red)' : undefined
              }}
            ></textarea>
            {showErrors && !noteCommitment.trim() && (
              <span style={{ color: 'var(--red)', fontSize: 12, marginTop: 4, display: 'block' }}>
                Vui lòng nhập cam kết cho ngày mai
              </span>
            )}
          </div>
          <div>
            <label className="field">Công thức tính điểm tuân thủ</label>
            <div className="formula">
              Điểm ngày = <b>trung bình cộng</b> điểm các lệnh<br />
              — <i>không nhân với P&L, không cộng thêm khi thắng</i><br />
              Kết quả lãi/lỗ hiển thị <b>song song</b>, tách biệt hoàn toàn khỏi điểm tuân thủ.
            </div>
          </div>
        </div>
      </div>

      {
        can_edit ? (
          <div className="footer-actions">
            <button className="btn btn-ghost" onClick={() => { setShowErrors(false); onReload?.(); }} disabled={isSaving}>Đặt lại</button>
            <button className="btn btn-primary" onClick={handleSaveEodReview} disabled={isSaving}>
              {isSaving ? 'Đang lưu...' : 'Lưu tổng kết ngày'}
            </button>
          </div>
        ) : (
          null
        )
      }

      {/* ==================== 6. STAGE WEIGHTS — reference detail of the day score, kept last ==================== */}
      <div className="card">
        <div className="card-head">
          <div>
            <h2><span className="num">6</span>Cơ cấu tính điểm các giai đoạn</h2>
            <p>Điểm tuân thủ tổng là tổng có trọng số của các giai đoạn trong ngày giao dịch.</p>
          </div>
        </div>

        <div className="stage-bar">
          {(eodSummary?.phases_breakdown || []).map((phase, idx) => {
            const numOnly = phase.weight_text ? phase.weight_text.replace(/[^0-9.]/g, '') : '';
            const widthCss = numOnly ? `${numOnly}%` : '25%';
            const displayLabel = numOnly ? `${numOnly}%` : phase.weight_text;

            return (
              <div
                key={phase.id || idx}
                className="stage-seg"
                style={{
                  width: widthCss,
                  background: idx === 0 ? '#7C93B8' : idx === 1 ? '#2E5CE6' : idx === 2 ? 'var(--amber)' : '#9B5DE5'
                }}
                title={`${phase.title}: ${displayLabel}`}
              >
                {displayLabel}
              </div>
            );
          })}
        </div>

        <div className="stage-list">
          {(eodSummary?.phases_breakdown || []).map((phase, index) => {
            const isExpanded = expandedStages.has(index);
            const dotColor = index === 0 ? '#7C93B8' : index === 1 ? '#2E5CE6' : index === 2 ? 'var(--amber)' : '#9B5DE5';
            const numOnly = phase.weight_text ? phase.weight_text.replace(/[^0-9.]/g, '') : '';
            const weightDisplay = numOnly ? `${numOnly}%` : phase.weight_text;

            return (
              <div className="stage-item-wrap" key={phase.id || index}>
                <div className="stage-row">
                  <div className="stage-dot" style={{ background: dotColor }}></div>
                  <div className="stage-info">
                    <div className="stage-name">
                      {phase.title} <span className="stage-w">trọng số {weightDisplay}</span>
                    </div>
                    <div className="stage-desc">{phase.desc}</div>
                    <div className="stage-track">
                      <div className="stage-fill" style={{ width: `${phase.percent}%`, background: dotColor }}></div>
                    </div>
                  </div>

                  <button
                    type="button"
                    className="!text-sm !font-medium text-brand-600 hover:text-brand-800 transition-colors"
                    onClick={() => toggleStageExpand(index)}
                    title="Xem chi tiết"
                  >
                    {isExpanded ? 'Ẩn chi tiết ▲' : 'Chi tiết ▼'}
                  </button>
                  <div className="stage-score">
                    <div className="ss-pts">{phase.earned_score.toFixed(1)} <span>/ {phase.max_score}đ</span></div>
                    <div className="ss-pct">{phase.percent}%</div>
                  </div>
                </div>

                {isExpanded && (
                  <div className="stage-detail-content" style={{ marginTop: '12px', padding: '12px 16px', background: '#f8fafc', borderRadius: '8px' }}>
                    <div className="plan-list" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {(phase.items || []).map((item: any, itemIdx: number) => {
                        const hasRatio = typeof item.pass_count === 'number' && typeof item.total_count === 'number' && item.total_count > 0;

                        const passCount = hasRatio ? item.pass_count : (item.is_passed ? 1 : 0);
                        const totalCount = hasRatio ? item.total_count : 1;
                        const ratio = Math.min(Math.max(passCount / totalCount, 0), 1);
                        const isFull = hasRatio ? (passCount === totalCount) : item.is_passed;

                        const circleSize = 22;
                        const strokeWidth = 2.5;
                        const center = circleSize / 2;
                        const radius = center - strokeWidth;
                        const circumference = 2 * Math.PI * radius;
                        const strokeDashoffset = circumference - ratio * circumference;

                        const unit = item.unit || (item.label?.toLowerCase().includes('kết quả') ? 'lệnh đã đóng' : 'lệnh');

                        return (
                          <div
                            key={itemIdx}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '12px 16px',
                              background: '#fff',
                              borderRadius: '8px',
                              border: '1px solid #e2e8f0',
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                              {isFull ? (
                                <div style={{
                                  width: circleSize,
                                  height: circleSize,
                                  borderRadius: '50%',
                                  background: '#00c39a',
                                  color: '#fff',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '12px',
                                  fontWeight: 'bold',
                                  flexShrink: 0
                                }}>
                                  ✓
                                </div>
                              ) : (
                                <div style={{ position: 'relative', width: circleSize, height: circleSize, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                  <svg width={circleSize} height={circleSize} style={{ transform: 'rotate(-90deg)' }}>
                                    <circle
                                      cx={center}
                                      cy={center}
                                      r={radius}
                                      fill="transparent"
                                      stroke="#e2e8f0"
                                      strokeWidth={strokeWidth}
                                    />
                                    <circle
                                      cx={center}
                                      cy={center}
                                      r={radius}
                                      fill="transparent"
                                      stroke="#00c39a"
                                      strokeWidth={strokeWidth}
                                      strokeDasharray={circumference}
                                      strokeDashoffset={strokeDashoffset}
                                      strokeLinecap="round"
                                      style={{ transition: 'stroke-dashoffset 0.4s ease-in-out' }}
                                    />
                                  </svg>
                                </div>
                              )}

                              <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <span style={{ fontSize: '14px', fontWeight: 600, color: '#1e293b' }}>
                                  {item.label}
                                </span>
                                {item.sub_label && (
                                  <span style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                                    {item.sub_label}
                                  </span>
                                )}
                              </div>
                            </div>

                            {hasRatio && (
                              <div>
                                <span style={{
                                  padding: '4px 12px',
                                  borderRadius: '14px',
                                  fontSize: '12.5px',
                                  fontWeight: 600,
                                  background: isFull ? '#e6f7f4' : '#fef3c7',
                                  color: isFull ? '#0d9488' : '#d97706',
                                }}>
                                  {passCount}/{totalCount} {unit}
                                </span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="stage-total">
          <div className="st-lbl">Tổng điểm các giai đoạn</div>
          <div className="st-formula">
            {(eodSummary?.phases_breakdown || []).map(p => p.earned_score.toFixed(1)).join(' + ')}
          </div>
          {/* The sum of the stages listed above; dayScore (the gauge in section 1) is the separate compliance score,
              which made this line read "0.0 + 0.0 + 0.0 + 0.0 = 17.0" */}
          <div className="st-val">{stageTotal.toFixed(1)} <span>/ 100đ</span></div>
        </div>
      </div>

    </div>
  );
};

export default StepEndOfDay_v2;